import React, { useState, useEffect, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import { fetchDomesByHost, fetchDetailedReport } from "../../../../api/reports.api";
import { Dropdown, Row, Col, Card, Nav, Button } from "react-bootstrap";
import { ThemeContext } from "../../../../context/ThemeContext";
import { exportReportToExcel } from "../../../../utils/excelExport.util";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const reportList = [
  "Usage Frequency",
  "Time of Day / Day of Week Usage",
  "Daily Utilization Report",
  "Idle Time Reporting",
  "Peak Usage Times",
  "Session Growth Rate Report",
  "Weekend vs Weekday Usage",
  "Session Density",
  "Usage Saturation Report",
  "High vs Low Demand Days / Weeks / Months",
  "Session Succession Report"
];

const ReportsMain = () => {
  const { background } = useContext(ThemeContext);
  const isDark = background.value === "dark";

  const [reportName, setReportName] = useState(reportList[0]);
  const [domes, setDomes] = useState([]);
  const [selectedHostId, setSelectedHostId] = useState(null);
  const [selectedDome, setSelectedDome] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [interval, setInterval] = useState("day");
  // Local date range for Utilization report, defaulting to last 5 days
  const [utilizationStart, setUtilizationStart] = useState(new Date(new Date().setDate(new Date().getDate() - 5)));
  const [utilizationEnd, setUtilizationEnd] = useState(new Date());

  // 1. Get Logged In Host automatically
  // 1. Get Logged In Host automatically
  useEffect(() => {
    try {
      const userDetailsStr = localStorage.getItem("hostDetails");
      console.log("DEBUG: ReportsMain userDetailsStr:", userDetailsStr);
      if (userDetailsStr) {
        const user = JSON.parse(userDetailsStr);
        console.log("DEBUG: Parsed User:", user);
        // Try _id first (Backend Mongo ID), then fallbacks
        if (user && (user.hostId || user._id)) {
          setSelectedHostId(user.hostId || user._id);
        } else if (user && user.id) {
          setSelectedHostId(user.id);
        } else if (user && user.companyId) {
          // Note: companyId might be different from _id, but trying it as fallback
          setSelectedHostId(user.companyId);
        }
      } else {
        console.warn("DEBUG: No hostDetails found in localStorage");
      }
    } catch (err) {
      console.error("Error parsing user details for reports", err);
    }
  }, []);

  // 2. Load Domes when Host ID is set
  useEffect(() => {
    if (selectedHostId) {
      loadDomes(selectedHostId);
    }
  }, [selectedHostId]);

  // 3. Load Report when dependencies change
  useEffect(() => {
    if (selectedHostId) {
      loadReport();
    }
  }, [reportName, selectedHostId, selectedDome, interval, utilizationStart, utilizationEnd]);

  const loadDomes = async (hostId) => {
    try {
      const d = await fetchDomesByHost(hostId);
      setDomes(d || []);
    } catch (error) {
      console.error("Error loading domes", error);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const result = await fetchDetailedReport(reportName, {
        hostRef: selectedHostId,
        domeRef: selectedDome?._id,
        interval,
        startDate: reportName === "Daily Utilization Report" ? utilizationStart.toISOString() : undefined,
        endDate: reportName === "Daily Utilization Report" ? utilizationEnd.toISOString() : undefined
      });
      setData(result);
    } catch (error) {
      console.error("Error loading report", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (data) {
      exportReportToExcel(reportName, data);
    }
  };

  const formatDuration = (totalMinutes) => {
    if (!totalMinutes && totalMinutes !== 0) return "0m";
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const formatMinutesToTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    const meridian = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${meridian}`;
  };

  const renderSummaryCard = (title, value, subtitle, variant = "primary") => (
    <Col xl={4} md={6} className="mb-4">
      <Card className="text-center overflow-hidden h-100 shadow-sm border-0">
        <Card.Body>
          <h6 className="mb-1 text-muted small uppercase fw-bold">{title}</h6>
          <h2 className={`text-${variant} mb-1`}>{value}</h2>
          <p className="text-muted mb-0 small">{subtitle}</p>
        </Card.Body>
        <div className={`bg-${variant}`} style={{ height: '4px' }}></div>
      </Card>
    </Col>
  );

  const renderReportContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="text-center py-5">
          <h4 className="text-muted">No data available.</h4>
        </div>
      );
    }

    switch (reportName) {
      case "Usage Frequency": return renderUsageFrequency();
      case "Time of Day / Day of Week Usage": return renderTimeOfDay();
      case "Daily Utilization Report": return renderUtilization();
      case "Idle Time Reporting": return renderIdleTime();
      case "Peak Usage Times": return renderPeakUsage();
      case "Session Growth Rate Report": return renderGrowthRate();
      case "Weekend vs Weekday Usage": return renderWeekendWeekday();
      case "Session Density": return renderDensity();
      case "Usage Saturation Report": return renderSaturation();
      case "High vs Low Demand Days / Weeks / Months": return renderHighLowDemand();
      case "Session Succession Report": return renderSuccession();
      default: return null;
    }
  };

  // --- Specific Report Renderers (Ported from Admin) ---
  function renderUsageFrequency() {
    return (
      <>
        <Row>
          {renderSummaryCard("Total Sessions", data?.metrics?.["Total Sessions"] || 0, "Cumulative sessions")}
          {renderSummaryCard("Avg Sessions", data?.metrics?.["Average Sessions per Period"] || 0, `Average per ${interval}`)}
          {renderSummaryCard("Total Periods", data?.metrics?.["Total Periods Tracked"] || 0, `${interval}s in range`)}
        </Row>
        <Card className="shadow-sm border-0">
          <Card.Header className="border-0 d-flex justify-content-between align-items-center pt-4">
            <h4 className="mb-0">Usage Trends</h4>
            <Nav variant="pills" activeKey={interval} onSelect={(k) => setInterval(k)}>
              <Nav.Item><Nav.Link eventKey="day" className="btn-xs">Daily</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="week" className="btn-xs">Weekly</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="month" className="btn-xs">Monthly</Nav.Link></Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            <ReactApexChart
              options={{
                theme: { mode: isDark ? 'dark' : 'light' },
                chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
                xaxis: { categories: data?.trends?.map(t => t._id) || [] },
                colors: ['#3a7afe'],
                plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } }
              }}
              series={[{ name: 'Sessions', data: data?.trends?.map(t => t.count) || [] }]}
              type="bar" height={350}
            />
          </Card.Body>
        </Card>
      </>
    );
  }

  function renderTimeOfDay() {
    return (
      <Row>
        <Col xl={6}>
          <Card className="shadow-sm border-0 mb-4 h-100">
            <Card.Header className="border-0 pt-4"><h4>Hourly Breakdown</h4></Card.Header>
            <Card.Body>
              <ReactApexChart
                options={{
                  theme: { mode: isDark ? 'dark' : 'light' },
                  chart: { type: 'area', toolbar: { show: false }, background: 'transparent' },
                  xaxis: { categories: Array.from({ length: 24 }, (_, i) => `${i}:00`) },
                  colors: ['#20c997'],
                  stroke: { curve: 'smooth', width: 3 }
                }}
                series={[{ name: 'Sessions', data: Array.from({ length: 24 }, (_, i) => (data?.hourly?.find(h => h._id === i)?.count || 0)) }]}
                type="area" height={350}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="shadow-sm border-0 mb-4 h-100">
            <Card.Header className="border-0 pt-4"><h4>Popular Days</h4></Card.Header>
            <Card.Body>
              <ReactApexChart
                options={{
                  theme: { mode: isDark ? 'dark' : 'light' },
                  chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
                  xaxis: { categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] },
                  colors: ['#3a7afe'],
                  plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } }
                }}
                series={[{ name: 'Sessions', data: [1, 2, 3, 4, 5, 6, 7].map(d => (data?.daily?.find(h => h._id === d)?.count || 0)) }]}
                type="bar" height={350}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  function renderUtilization() {
    return (
      <Card className="shadow-sm border-0">
        <Card.Header className="border-0 pt-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h4 className="mb-0">Daily Utilization (Booked vs Empty)</h4>
          <div className="d-flex align-items-center gap-2">
            <DatePicker
              selected={utilizationStart}
              onChange={(date) => setUtilizationStart(date)}
              selectsStart
              startDate={utilizationStart}
              endDate={utilizationEnd}
              className="form-control form-control-sm"
              dateFormat="MMM d, yyyy"
            />
            <span className="text-muted">-</span>
            <DatePicker
              selected={utilizationEnd}
              onChange={(date) => setUtilizationEnd(date)}
              selectsEnd
              startDate={utilizationStart}
              endDate={utilizationEnd}
              minDate={utilizationStart}
              className="form-control form-control-sm"
              dateFormat="MMM d, yyyy"
            />
          </div>
        </Card.Header>
        <Card.Body>
          <ReactApexChart
            options={{
              theme: { mode: isDark ? 'dark' : 'light' },
              chart: { type: 'bar', stacked: true, toolbar: { show: false }, background: 'transparent' },
              xaxis: { categories: data?.utilization?.map(u => u.date) || [] },
              colors: ['#3a7afe', isDark ? '#444' : '#e6e6e6'],
              plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } },
              dataLabels: { enabled: false },
              tooltip: { y: { formatter: (val) => `${val} Sessions` } },
              yaxis: { title: { text: 'Number of Sessions' } }
            }}
            series={[
              { name: 'Booked Sessions', data: data?.utilization?.map(u => u.booked) || [] },
              { name: 'Empty Slots', data: data?.utilization?.map(u => u.empty) || [] }
            ]}
            type="bar" height={400}
          />
        </Card.Body>
      </Card>
    );
  }

  function renderIdleTime() {
    return (
      <>
        <Row>
          {renderSummaryCard("Avg Daily Idle", formatDuration(data?.metrics?.["Average Idle per Dome"]), "Per Dome average", "info")}
          {renderSummaryCard("Total Sessions", data?.metrics?.["Total Sessions"] || 0, "Scheduled slots", "primary")}
          {renderSummaryCard("Total Domes", data?.metrics?.["Total Domes Tracked"] || 0, "Domes analyzed", "success")}
        </Row>

        {data?.domes?.map((dome, idx) => (
          <Card key={idx} className="shadow-sm border-0 mb-4 overflow-hidden">
            <Card.Header className="border-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{dome.domeName}</h5>
              <span className="text-muted small">Total Idle: <strong>{formatDuration(dome.totalIdleMinutes)}</strong></span>
            </Card.Header>
            <Card.Body>
              <label className="text-muted small fw-bold mb-2">24-HOUR TIMELINE</label>
              <div className="d-flex w-100 rounded-3 overflow-hidden border" style={{ height: '40px', backgroundColor: isDark ? '#2c2c2c' : '#f8f9fa' }}>
                {dome.timeline?.map((block, bIdx) => {
                  const widthPct = (block.duration / 1440) * 100;
                  return (
                    <div
                      key={bIdx}
                      className={`timeline-block ${block.type}`}
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: block.type === 'session' ? '#3a7afe' : (isDark ? 'transparent' : '#f8f9fa'),
                        borderRight: bIdx < dome.timeline.length - 1 ? '1px solid rgba(128,128,128,0.2)' : 'none',
                      }}
                      title={`${block.type === 'session' ? (block.name || 'Session') : 'Idle'}: ${formatDuration(block.duration)}`}
                    />
                  );
                })}
              </div>
              <div className="mt-3 d-flex gap-4">
                <div className="d-flex align-items-center gap-2">
                  <div className="rounded-circle" style={{ width: 12, height: 12, backgroundColor: '#3a7afe' }}></div>
                  <span className="small text-muted">Sessions</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="rounded-circle border" style={{ width: 12, height: 12, backgroundColor: '#f8f9fa' }}></div>
                  <span className="small text-muted">Idle</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </>
    );
  }

  function renderPeakUsage() {
    return (
      <Card className="shadow-sm border-0">
        <Card.Header className="border-0 pt-4"><h4>Peak Time Blocks (24h)</h4></Card.Header>
        <Card.Body>
          <ReactApexChart
            options={{
              theme: { mode: isDark ? 'dark' : 'light' },
              chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
              plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
              xaxis: {
                categories: data?.peaks?.map(p => {
                  const h = p._id;
                  return h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
                }) || [],
                title: { text: 'Time of Day' }
              },
              colors: ['#ea5455'],
              yaxis: { title: { text: 'Total Sessions' } }
            }}
            series={[{ name: 'Sessions', data: data?.peaks?.map(p => p.count) || [] }]}
            type="bar" height={450}
          />
        </Card.Body>
      </Card>
    );
  }

  function renderGrowthRate() {
    return (
      <Card className="shadow-sm border-0">
        <Card.Header className="border-0 pt-4"><h4>Monthly Session Trends</h4></Card.Header>
        <Card.Body>
          <ReactApexChart
            options={{
              theme: { mode: isDark ? 'dark' : 'light' },
              chart: { type: 'line', toolbar: { show: false }, background: 'transparent' },
              xaxis: { categories: data?.monthly?.map(m => m._id) || [] },
              colors: ['#20c997'],
              stroke: { curve: 'smooth', width: 3 }
            }}
            series={[{ name: 'Sessions', data: data?.monthly?.map(m => m.count) || [] }]}
            type="line" height={350}
          />
        </Card.Body>
      </Card>
    );
  }

  function renderWeekendWeekday() {
    return (
      <Row>
        <Col xl={6}>
          <Card className="shadow-sm border-0 mb-4 h-100">
            <Card.Header className="border-0 pt-4"><h4>Volume Comparison</h4></Card.Header>
            <Card.Body>
              <ReactApexChart
                options={{
                  theme: { mode: isDark ? 'dark' : 'light' },
                  chart: { type: 'donut', background: 'transparent' },
                  labels: ['Weekday', 'Weekend'],
                  colors: ['#3a7afe', '#ff9f43'],
                  legend: { position: 'bottom' }
                }}
                series={[
                  data?.stats?.find(s => !s._id)?.avgCount || 0,
                  data?.stats?.find(s => s._id)?.avgCount || 0
                ]}
                type="donut" width="100%" height={350}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="shadow-sm border-0 mb-4 h-100">
            <Card.Header className="border-0 pt-4"><h4>Average Duration</h4></Card.Header>
            <Card.Body>
              <ReactApexChart
                options={{
                  theme: { mode: isDark ? 'dark' : 'light' },
                  chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
                  xaxis: { categories: ['Weekday', 'Weekend'] },
                  colors: ['#20c997', '#ea5455']
                }}
                series={[{
                  name: 'Avg Duration',
                  data: [
                    data?.stats?.find(s => !s._id)?.avgDuration || 0,
                    data?.stats?.find(s => s._id)?.avgDuration || 0
                  ]
                }]}
                type="bar" height={350}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  function renderDensity() {
    return (
      <Card className="shadow-sm border-0">
        <Card.Header className="border-0 pt-4"><h4>Session Density Heatmap</h4></Card.Header>
        <Card.Body>
          <ReactApexChart
            options={{
              theme: { mode: isDark ? 'dark' : 'light' },
              chart: { type: 'heatmap', toolbar: { show: false }, background: 'transparent' },
              xaxis: { categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] },
              colors: ["#3a7afe"],
              plotOptions: { heatmap: { radius: 2, enableShades: true } }
            }}
            series={Array.from({ length: 24 }, (_, h) => ({
              name: `${h}:00`,
              data: [1, 2, 3, 4, 5, 6, 7].map(d => ({
                x: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d - 1],
                y: data?.density?.find(item => item._id.hour === h && item._id.day === d)?.count || 0
              }))
            }))}
            type="heatmap" height={500}
          />
        </Card.Body>
      </Card>
    );
  }

  function renderSaturation() {
    return (
      <Row>
        <Col xl={5}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="border-0 pt-4"><h4>Usage Saturation</h4></Card.Header>
            <Card.Body>
              <ReactApexChart
                options={{
                  theme: { mode: isDark ? 'dark' : 'light' },
                  chart: { type: 'donut', background: 'transparent' },
                  labels: ['Low Used (<50%)', 'Moderately Used (50-80%)', 'Highly Used (>80%)'],
                  colors: ['#ea5455', '#ff9f43', '#20c997'],
                  legend: { position: 'bottom' }
                }}
                series={[
                  data?.summary?.["Low (<50%)"] || 0,
                  data?.summary?.["Medium (50-80%)"] || 0,
                  data?.summary?.["High (>80%)"] || 0
                ]}
                type="donut" height={300}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xl={7}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Header className="border-0 pt-4">
              <h4 className="mb-0">Daily Details</h4>
            </Card.Header>
            <Card.Body className="p-0 table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table className="table table-responsive-mobile table-sm table-striped mb-0">
                <thead className={`${isDark ? 'text-white' : 'bg-light'} sticky-top`} style={{ zIndex: 1, backgroundColor: isDark ? '#1a1a1a' : '#f8f9fa' }}>
                  <tr>
                    <th>Date</th>
                    <th>Dome</th>
                    <th className="text-center">Cap.</th>
                    <th className="text-center">Booked</th>
                    <th className="text-end">Saturation</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.details?.map((item, i) => (
                    <tr key={i}>
                      <td data-label="Date">{item.date}</td>
                      <td data-label="Dome">{item.dome}</td>
                      <td data-label="Cap." className="text-center">{item.capacity}</td>
                      <td data-label="Booked" className="text-center">{item.booked}</td>
                      <td data-label="Saturation" className="text-end">
                        <span className={`badge badge-xs light badge-${item.bucket === 'High' ? 'success' : item.bucket === 'Medium' ? 'warning' : 'danger'}`}>
                          {item.percentage}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!data?.details?.length && <tr><td colSpan="5" className="text-center">No details available.</td></tr>}
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  function renderHighLowDemand() {
    return (
      <Row>
        <Col xl={6}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="border-0 d-flex justify-content-between pt-4">
              <h4>High Demand Peaks</h4>
              <span className="badge badge-success">Top 5 Days</span>
            </Card.Header>
            <Card.Body className="p-0">
              <ul className="list-group list-group-flush">
                {data?.highDemand?.map((d, i) => (
                  <li key={d._id} className="list-group-item d-flex justify-content-between align-items-center py-3">
                    <div className="d-flex align-items-center">
                      <span className="btn btn-primary btn-xs sharp rounded-circle me-3">{i + 1}</span>
                      <span className="fw-bold">{d._id}</span>
                    </div>
                    <span className="text-primary fw-bold mb-0">{d.count} sessions</span>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="border-0 d-flex justify-content-between pt-4">
              <h4>Low Demand Lulls</h4>
              <span className="badge badge-warning text-white">Trailing Days</span>
            </Card.Header>
            <Card.Body className="p-0">
              <ul className="list-group list-group-flush">
                {data?.lowDemand?.slice().reverse().map((d, i) => (
                  <li key={d._id} className="list-group-item d-flex justify-content-between align-items-center py-3">
                    <div className="d-flex align-items-center">
                      <span className="btn btn-warning btn-xs sharp rounded-circle me-3 text-white">{i + 1}</span>
                      <span className="fw-bold">{d._id}</span>
                    </div>
                    <span className="text-warning fw-bold mb-0">{d.count} sessions</span>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  function renderSuccession() {
    return (
      <>
        <div className="row">
          <Col xl={12}>
            <Card className="shadow-sm border-0 mb-4 bg-primary text-white">
              <Card.Body className="d-flex justify-content-between align-items-center p-4">
                <div>
                  <h4 className="text-white mb-1">Session Succession Rate</h4>
                  <p className="text-white-50 mb-0">Percentage of sessions starting within 30 mins of previous one.</p>
                </div>
                <div className="text-end">
                  <h2 className="text-white display-4 fw-bold mb-0">{data?.metrics?.["Succession Rate"] || "0%"}</h2>
                  <span className="text-white-50">{data?.metrics?.["Back-to-Back Sessions"] || 0} Back-to-Back Sessions</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </div>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="border-0 pt-4"><h4>Recent Back-to-Back Events</h4></Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-responsive-mobile table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Dome</th>
                    <th>Date</th>
                    <th>First Session</th>
                    <th>Second Session</th>
                    <th className="text-end">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.events?.map((ev, i) => (
                    <tr key={i}>
                      <td data-label="Dome" className="fw-bold">{ev.dome}</td>
                      <td data-label="Date">{ev.date}</td>
                      <td data-label="First Session">{ev.firstSession}</td>
                      <td data-label="Second Session">{ev.secondSession}</td>
                      <td data-label="Gap" className="text-end text-primary fw-bold">{ev.gap}</td>
                    </tr>
                  ))}
                  {(!data?.events || data.events.length === 0) && (
                    <tr><td colSpan="5" className="text-center py-4 text-muted">No back-to-back sessions found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </>
    );
  }

  if (!selectedHostId) {
    return (
      <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-3 text-muted">Initializing Host Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Top Filter Selection */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Row className="align-items-end">
            <Col xl={5} md={6} className="mb-2">
              <label className="text-muted small fw-bold">DOME (OPTIONAL)</label>
              <Dropdown onSelect={(id) => setSelectedDome(id === "all" ? null : domes.find(d => d._id === id))}>
                <Dropdown.Toggle variant="outline-primary" className="w-100 text-start d-flex justify-content-between align-items-center">
                  {selectedDome ? selectedDome.domeName : "All Domes"}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100 shadow-sm border-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Dropdown.Item eventKey="all">All Domes</Dropdown.Item>
                  <Dropdown.Divider />
                  {domes.map(d => <Dropdown.Item key={d._id} eventKey={d._id}>{d.domeName}</Dropdown.Item>)}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xl={7} md={6} className="mb-2">
              <label className="text-muted small fw-bold d-block">CHOOSE ANALYTIC REPORT</label>
              <div className="d-flex align-items-center gap-3">
                <div className="flex-grow-1">
                  <Dropdown onSelect={(name) => setReportName(name)} className="w-100">
                    <Dropdown.Toggle variant="primary" className="w-100 text-start d-flex justify-content-between align-items-center">
                      {reportName}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100 shadow-sm border-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {reportList.map(r => <Dropdown.Item key={r} eventKey={r}>{r}</Dropdown.Item>)}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <Button
                  variant="success"
                  onClick={handleExportExcel}
                  disabled={!data || loading}
                  className="d-flex align-items-center gap-2"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className="fa fa-file-excel"></i>
                  <span>Export Excel</span>
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="report-render-content">
        {renderReportContent()}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
                .cursor-pointer { cursor: pointer; }
                .transition-all { transition: all 0.2s ease; }
                .custom-scroll::-webkit-scrollbar { width: 5px; }
                .custom-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
                .custom-scroll::-webkit-scrollbar-thumb { background: #888; border-radius: 10px; }
                .btn-xs { padding: 0.25rem 0.5rem; font-size: 0.75rem; }
                .nav-pills .nav-link.active { background-color: #3167eb !important; color: #fff !important; }
                .nav-pills .nav-link { color: #6e6e6e; font-weight: 500; }
                .sharp { width: 30px; height: 30px; padding: 0; line-height: 30px; text-align: center; }
                .timeline-block { height: 100%; display: flex; align-items: center; transition: all 0.2s; }
                .timeline-block:hover { filter: brightness(0.95); }
                .extra-small { font-size: 0.65rem; }
            `}} />
    </div>
  );
};


export default ReportsMain;